import React, { useState, useEffect, useMemo } from 'react';
import { authFetch, logout } from '../lib/authFetch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DispatchNav from '../components/DispatchNav';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Search,
  Filter,
  Download,
  RefreshCw,
  CheckSquare,
  Square,
  LogOut,
  Home as HomeIcon,
} from 'lucide-react';

/** Return today's date in America/Chicago as YYYY-MM-DD. */
function chicagoToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

/** Priority order for the "needs action" default sort. */
const STATUS_PRIORITY = {
  'Pending Dispatch Review': 0, 'Pending': 0, 'PendingReview': 0, 'New': 0,
  'Contacted': 1,
  'Confirmed': 2,
  'Scheduled': 3, 'Assigned': 3,
  'DriverEnRoute': 4, 'InProgress': 4,
  'Completed': 5,
  'Declined': 6, 'Cancelled': 6, 'NoShow': 6,
};

const DispatchBookings = () => {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(urlParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all | today | upcoming | past
  const [modeFilter, setModeFilter] = useState('all');
  const [tripTypeFilter, setTripTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Apply URL `filter` param on mount and whenever it changes.
  useEffect(() => {
    const filter = urlParams.get('filter');
    if (!filter) return;
    if (filter === 'today') { setDateFilter('today'); setStatusFilter('all'); return; }
    if (filter === 'upcoming') { setDateFilter('upcoming'); setStatusFilter('all'); return; }
    if (filter === 'past') { setDateFilter('past'); setStatusFilter('all'); return; }
    if (filter.includes(',')) {
      // Multi-status filters like "Declined,Cancelled" — first one wins as active filter
      // but our select is single-value; show all of them by using All + user tweaks.
      setStatusFilter(filter.split(',')[0]);
      return;
    }
    setStatusFilter(filter);
  }, [urlParams]);

  const fetchBookings = React.useCallback(async () => {
    try {
      const response = await authFetch(`/api/bookings`);
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('[DispatchBookings] Failed to fetch bookings:', err);
      // Show error state to user
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auth is enforced server-side; authFetch handles the redirect on 401.
    fetchBookings();
  }, [navigate, fetchBookings]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Dispatch Review': 'bg-amber-100 text-amber-800',
      'Contacted': 'bg-sky-100 text-sky-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Confirmed': 'bg-blue-100 text-blue-800',
      'Assigned': 'bg-purple-100 text-purple-800',
      'DriverEnRoute': 'bg-green-100 text-green-800',
      'InProgress': 'bg-green-100 text-green-800',
      'Completed': 'bg-emerald-100 text-emerald-800',
      'Declined': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-rose-100 text-rose-800',
      'NoShow': 'bg-orange-100 text-orange-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const isPendingReview = (b) => b.status === 'Pending Dispatch Review';

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedBookings = bookings.filter((b) => selectedIds.has(b.id));

  const handleBulkContacted = async () => {
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const res = await authFetch('/api/bookings/bulk/mark-contacted', {
        method: 'POST',
        body: JSON.stringify({ booking_ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Bulk update failed.');
        return;
      }
      setBulkResult(data);
      if (data.failed === 0) toast.success(`${data.updated} request(s) marked Contacted.`);
      else toast.warning(`${data.updated} updated, ${data.failed} skipped. See details.`);
      // Refresh list to reflect new statuses
      await fetchBookings();
      // Drop successful ids from selection; keep the failures visible.
      const failedIds = new Set(
        (data.results || []).filter((r) => !r.ok).map((r) => r.booking_id)
      );
      setSelectedIds(failedIds);
    } catch (e) {
      toast.error('Network error during bulk update.');
    } finally {
      setBulkSaving(false);
      setShowBulkConfirm(false);
    }
  };

  const filteredBookings = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    const today = chicagoToday();
    const phoneDigits = q.replace(/[^0-9]/g, '');
    const matchesQuery = (b) => {
      if (!q) return true;
      const inStr = (v) => typeof v === 'string' && v.toLowerCase().includes(q);
      // Ref match: allow with or without leading #
      const refMatch = typeof b.ref === 'string' && b.ref.toLowerCase().includes(q.replace(/^#/, ''));
      // Phone match: strip non-digits and match substring
      const bPhone = typeof b.contact_phone === 'string' ? b.contact_phone.replace(/[^0-9]/g, '') : '';
      const phoneMatch = phoneDigits && bPhone && bPhone.includes(phoneDigits);
      return refMatch
        || inStr(b.patient_name)
        || inStr(b.customer_name)
        || inStr(b.pickup_address)
        || inStr(b.dropoff_address)
        || !!phoneMatch;
    };
    const matchesStatus = (b) => statusFilter === 'all' || b.status === statusFilter;
    const matchesDate = (b) => {
      if (dateFilter === 'all') return true;
      const d = b.date || '';
      if (dateFilter === 'today') return d === today;
      if (dateFilter === 'upcoming') return d > today;
      if (dateFilter === 'past') return d && d < today;
      return true;
    };
    const matchesMode = (b) => modeFilter === 'all' || b.mobility_type === modeFilter;
    const matchesTripType = (b) => tripTypeFilter === 'all' || b.trip_type === tripTypeFilter;

    return bookings
      .filter((b) => matchesQuery(b) && matchesStatus(b) && matchesDate(b) && matchesMode(b) && matchesTripType(b))
      .sort((a, b) => {
        // Priority 1: needs-action ordering by status
        const pa = STATUS_PRIORITY[a.status] ?? 9;
        const pb = STATUS_PRIORITY[b.status] ?? 9;
        if (pa !== pb) return pa - pb;
        // Priority 2: nearest transportation date
        return (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || '');
      });
  }, [bookings, searchTerm, statusFilter, dateFilter, modeFilter, tripTypeFilter]);

  // Collect distinct mobility types for the filter dropdown
  const modeOptions = useMemo(
    () => Array.from(new Set(bookings.map((b) => b.mobility_type).filter(Boolean))).sort(),
    [bookings]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F9A825] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#004C97] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/dispatch/dashboard')}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-bold text-white">All Bookings</h1>
              <Badge variant="secondary" className="bg-white text-[#004C97]">
                {filteredBookings.length} Total
              </Badge>
            </div>
            <Button
              onClick={async () => {
                try {
                  const p = new URLSearchParams();
                  const res = await authFetch(`/api/dispatch/exports/bookings.csv?${p.toString()}`);
                  if (!res.ok) { toast.error('Export failed'); return; }
                  const blob = await res.blob();
                  const disp = res.headers.get('content-disposition') || '';
                  const m = /filename="?([^"]+)"?/i.exec(disp);
                  const name = (m && m[1]) || 'transportation-requests.csv';
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success('Export downloaded');
                } catch { toast.error('Network error'); }
              }}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#004C97] ml-1"
              data-testid="bookings-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={fetchBookings}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#004C97]"
              data-testid="bookings-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={async () => { await logout(); }}
              variant="ghost"
              className="text-white hover:bg-white/10 ml-1"
              data-testid="bookings-logout-btn"
              title="Log out"
            >
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <DispatchNav />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ref, customer, or phone"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    const next = new URLSearchParams(urlParams);
                    if (e.target.value) next.set('q', e.target.value); else next.delete('q');
                    setUrlParams(next, { replace: true });
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
                  data-testid="bookings-search-input"
                />
              </div>

              {/* Quick date filters (Today / Upcoming / Past) */}
              <div className="flex flex-wrap gap-2" role="group" aria-label="Date filter">
                {[
                  { key: 'all', label: 'All Dates' },
                  { key: 'today', label: 'Today' },
                  { key: 'upcoming', label: 'Upcoming' },
                  { key: 'past', label: 'Past' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setDateFilter(opt.key)}
                    className={`min-h-[36px] px-3 rounded-full border text-sm ${
                      dateFilter === opt.key
                        ? 'bg-[#004C97] text-white border-[#004C97]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#004C97]'
                    }`}
                    data-testid={`bookings-date-filter-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Advanced filters row */}
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent text-sm"
                  data-testid="bookings-status-filter"
                >
                  <option value="all">All Statuses</option>
                  <option value="Pending Dispatch Review">Pending Dispatch Review</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Declined">Declined</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending (legacy)</option>
                  <option value="Assigned">Assigned (legacy)</option>
                  <option value="DriverEnRoute">En Route (legacy)</option>
                  <option value="InProgress">In Progress (legacy)</option>
                  <option value="NoShow">No Show (legacy)</option>
                </select>

                <select
                  value={tripTypeFilter}
                  onChange={(e) => setTripTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] text-sm"
                  data-testid="bookings-trip-type-filter"
                >
                  <option value="all">All Trip Types</option>
                  <option value="one_way">One-way</option>
                  <option value="round_trip">Round-trip</option>
                  <option value="wait_and_return">Wait &amp; Return</option>
                </select>

                <select
                  value={modeFilter}
                  onChange={(e) => setModeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] text-sm"
                  data-testid="bookings-mode-filter"
                >
                  <option value="all">All Transportation Modes</option>
                  {modeOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk action bar (shown when selection is non-empty) */}
        {selectedIds.size > 0 && (
          <Card className="mb-4 border-[#004C97]" data-testid="bulk-action-bar">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
              <div>
                <p className="text-sm font-semibold text-[#004C97]">
                  {selectedIds.size} request(s) selected
                </p>
                <p className="text-xs text-gray-600">
                  Bulk actions are limited to marking <strong>Pending Dispatch Review → Contacted</strong>.
                  Requests in any other status will be skipped.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearSelection} data-testid="bulk-clear-btn">
                  Clear
                </Button>
                <Button
                  onClick={() => setShowBulkConfirm(true)}
                  className="bg-[#004C97] hover:bg-[#003a78] text-white"
                  data-testid="bulk-mark-contacted-btn"
                >
                  Mark Contacted
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk result panel */}
        {bulkResult && (
          <Card className="mb-4" data-testid="bulk-result-panel">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-gray-900">
                Bulk update result: {bulkResult.updated} updated · {bulkResult.failed} failed
              </p>
              {bulkResult.failed > 0 && (
                <ul className="mt-2 text-xs text-gray-700 space-y-1">
                  {bulkResult.results.filter((r) => !r.ok).map((r) => (
                    <li key={r.booking_id} data-testid={`bulk-result-fail-${r.booking_id}`}>
                      <span className="font-mono">{r.ref || r.booking_id.slice(0, 8)}</span> — {r.reason}
                      {r.current_status ? ` (currently ${r.current_status})` : ''}
                    </li>
                  ))}
                </ul>
              )}
              <Button variant="ghost" size="sm" onClick={() => setBulkResult(null)} className="mt-2">
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle>Booking Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-8">
                      <span className="sr-only">Select</span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer / Patient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Route
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Mobility
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      className={`hover:bg-gray-50 ${selectedIds.has(booking.id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-2 py-4 align-middle">
                        <button
                          type="button"
                          onClick={() => isPendingReview(booking) && toggleSelected(booking.id)}
                          disabled={!isPendingReview(booking)}
                          className={`w-6 h-6 flex items-center justify-center rounded ${
                            isPendingReview(booking)
                              ? 'text-[#004C97] hover:bg-blue-100'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title={isPendingReview(booking)
                            ? 'Select for bulk Mark Contacted'
                            : 'Only Pending Dispatch Review requests can be bulk-selected'}
                          data-testid={`row-checkbox-${booking.id}`}
                        >
                          {selectedIds.has(booking.id)
                            ? <CheckSquare className="w-5 h-5" />
                            : <Square className="w-5 h-5" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        #{booking.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-semibold text-[#004C97]">
                            {booking.customer_name || booking.patient_name || '—'}
                          </p>
                          <p className="text-xs text-gray-600">{booking.patient_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{booking.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{booking.time}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 max-w-xs">
                        <div className="flex items-start space-x-1">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-gray-600">
                            <p className="line-clamp-1">{booking.pickup_address}</p>
                            <p className="line-clamp-1">→ {booking.dropoff_address || booking.destination}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">{booking.mobility_type}</span>
                      </td>
                      <td className="px-4 py-4">
                        {booking.assigned_driver_name ? (
                          <span className="text-sm text-green-600 font-medium">
                            {booking.assigned_driver_name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(booking.status)}
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dispatch/bookings/${booking.id}`)}
                          data-testid={`row-view-btn-${booking.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredBookings.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No bookings found matching your criteria.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bulk confirmation modal */}
        {showBulkConfirm && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            data-testid="bulk-confirm-modal"
          >
            <Card className="w-full max-w-lg">
              <CardContent className="p-5 space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Mark {selectedBookings.length} request(s) as Contacted?
                </h3>
                <p className="text-sm text-gray-600">
                  This is a bulk update from <strong>Pending Dispatch Review → Contacted</strong>.
                  Confirming, declining, cancelling, or completing a trip must be done
                  individually so dispatch can verify driver, vehicle, mode, time, service
                  area, and mobility needs.
                </p>
                <div className="max-h-40 overflow-y-auto border rounded-lg bg-gray-50 p-2" data-testid="bulk-confirm-refs">
                  <ul className="text-xs text-gray-800 space-y-1 font-mono">
                    {selectedBookings.map((b) => (
                      <li key={b.id}>
                        {b.ref || b.id.slice(0, 8).toUpperCase()} — {b.status}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowBulkConfirm(false)}
                    disabled={bulkSaving}
                    data-testid="bulk-confirm-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleBulkContacted}
                    disabled={bulkSaving}
                    className="bg-[#004C97] hover:bg-[#003a78] text-white disabled:opacity-50"
                    data-testid="bulk-confirm-submit-btn"
                  >
                    {bulkSaving ? 'Updating…' : 'Yes, mark Contacted'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default DispatchBookings;
